import { computed, type ComputedRef, type Ref } from 'vue'
import type { WidgetTemplateColumnWithConfig } from '@/types/dashboard'
import { getCellValue, matchesFilter } from '@/helpers/tableFilterUtils'

/**
 * Composable for table filtering logic
 * Applies filters with AND logic across all active column filters
 */
export function useTableFilters(
    tableData: ComputedRef<Record<string, unknown>[]> | Ref<Record<string, unknown>[]>,
    columns: ComputedRef<WidgetTemplateColumnWithConfig[]> | Ref<WidgetTemplateColumnWithConfig[]>,
    columnFilters: Ref<Record<string, Array<string | number>>>
) {
    const filteredTableData = computed(() => {
        if (!tableData.value?.length) {
            return tableData.value
        }

        // Get active filters (non-empty)
        const activeFilters = Object.entries(columnFilters.value).filter(
            ([, values]) => values.length > 0
        )

        if (!activeFilters.length) {
            return tableData.value
        }

        const columnsMap = new Map(
            columns.value.map(col => [col.prop, col])
        )

        // Apply filters with AND logic
        return tableData.value.filter(row => {
            return activeFilters.every(([columnProp, filterValues]) => {
                const column = columnsMap.get(columnProp)
                if (!column?.filterableConfig) {
                    return true
                }

                return matchesFilter(
                    getCellValue(row, columnProp),
                    column.filterableConfig,
                    filterValues
                )
            })
        })
    })

    return {
        filteredTableData
    }
}
